import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Share2,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Link2,
} from "lucide-react";
import type { SocialLink } from "@/hooks/use-supabase-data";

interface SocialLinkFormState {
  platform: string;
  url: string;
  icon: string;
  display_order: number | null;
  is_active: boolean;
}

const iconOptions = [
  { value: "facebook", label: "Facebook" },
  { value: "x", label: "X (formerly Twitter)" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "youtube", label: "YouTube" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "threads", label: "Threads" },
  { value: "website", label: "Website" },
  { value: "twitter", label: "Twitter (legacy)" },
];

const defaultFormState: SocialLinkFormState = {
  platform: "",
  url: "",
  icon: "facebook",
  display_order: null,
  is_active: true,
};

export function SocialLinksManager() {
  const [links, setLinks] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<SocialLink | null>(null);
  const [formState, setFormState] = useState<SocialLinkFormState>(defaultFormState);

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const { data, error } = await supabase
        .from("social_links")
        .select("*")
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) throw error;
      setLinks(data || []);
    } catch (_err) {
      setError("Failed to load social links. Please try again.");
      setLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      if (editingLink?.id) {
        const { error } = await supabase
          .from("social_links")
          .update({
            ...formState,
            display_order: formState.display_order ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingLink.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("social_links")
          .insert({
            ...formState,
            display_order: formState.display_order ?? null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

        if (error) throw error;
      }

      setSuccess("Social link saved successfully.");
      await fetchSocialLinks();
      handleCloseDialog();
    } catch (_err) {
      setError("Unable to save the social link. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("social_links").delete().eq("id", id);
      if (error) throw error;

      setSuccess("Social link deleted.");
      await fetchSocialLinks();
    } catch (_err) {
      setError("Failed to delete social link. Please try again.");
    }
  };

  const handleToggleActive = async (link: SocialLink, nextValue: boolean) => {
    try {
      const { error } = await supabase
        .from("social_links")
        .update({
          is_active: nextValue,
          updated_at: new Date().toISOString(),
        })
        .eq("id", link.id);

      if (error) throw error;

      setLinks((prev) =>
        prev.map((item) =>
          item.id === link.id
            ? {
                ...item,
                is_active: nextValue,
              }
            : item,
        ),
      );
    } catch (_err) {
      setError("Unable to update the link status.");
    }
  };

  const handleEdit = (link: SocialLink) => {
    setEditingLink(link);
    setFormState({
      platform: link.platform ?? "",
      url: link.url ?? "",
      icon: link.icon === "twitter" ? "x" : link.icon ?? "facebook",
      display_order: link.display_order ?? null,
      is_active: link.is_active ?? true,
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingLink(null);
    setFormState(defaultFormState);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingLink(null);
    setFormState(defaultFormState);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
            Social Links Management
          </h2>
          <p className="text-slate-600 text-lg">
            Control the social icons and destinations displayed in the site footer.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={fetchSocialLinks}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Link
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-emerald-200 bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            Social Profiles ({links.length})
          </CardTitle>
          <CardDescription>Display order controls how the icons appear in the footer.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading social links...</p>
              </div>
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-10">
              <Share2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No social links added yet.</p>
              <Button onClick={handleAddNew}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Link
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Platform</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead className="hidden md:table-cell">Icon</TableHead>
                  <TableHead className="hidden md:table-cell">Display Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {links.map((link) => (
                  <TableRow key={link.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">
                          {iconOptions.find((opt) => opt.value === (link.icon === "twitter" ? "x" : link.icon))?.label ??
                            link.icon ??
                            "icon"}
                        </Badge>
                        <span className="font-medium">{link.platform}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <Link2 className="w-3 h-3" />
                        {link.url}
                      </a>
                    </TableCell>
                    <TableCell className="hidden md:table-cell capitalize">
                      {iconOptions.find((opt) => opt.value === (link.icon === "twitter" ? "x" : link.icon))?.label ??
                        link.icon ??
                        "—"}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {typeof link.display_order === "number" ? link.display_order : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={link.is_active ?? true}
                          onCheckedChange={(value) => handleToggleActive(link, value)}
                        />
                        <span className="text-sm text-muted-foreground">
                          {link.is_active ?? true ? "Active" : "Hidden"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(link)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete social link?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. The selected social profile will be permanently removed
                                from the footer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                                onClick={() => handleDelete(link.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingLink ? "Edit Social Link" : "Add Social Link"}</DialogTitle>
            <DialogDescription>
              {editingLink ? "Update the selected social profile details." : "Provide the information for the new social profile."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="platform">Platform</Label>
                <Input
                  id="platform"
                  value={formState.platform}
                  onChange={(event) => setFormState({ ...formState, platform: event.target.value })}
                  placeholder="Facebook"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <select
                  id="icon"
                  value={formState.icon}
                  onChange={(event) => setFormState({ ...formState, icon: event.target.value })}
                  className="w-full px-3 py-2 border border-input bg-background rounded-md"
                >
                  {iconOptions.map((icon) => (
                    <option key={icon.value} value={icon.value}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Profile URL</Label>
              <Input
                id="url"
                value={formState.url}
                onChange={(event) => setFormState({ ...formState, url: event.target.value })}
                placeholder="https://facebook.com/shantinagar"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  min={0}
                  value={formState.display_order ?? ""}
                  onChange={(event) =>
                    setFormState({
                      ...formState,
                      display_order: event.target.value === "" ? null : Number(event.target.value),
                    })
                  }
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="is_active">Visibility</Label>
                <div className="flex items-center gap-2 rounded-md border border-input px-3 py-2">
                  <Switch
                    id="is_active"
                    checked={formState.is_active}
                    onCheckedChange={(value) => setFormState({ ...formState, is_active: value })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {formState.is_active ? "Active" : "Hidden"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

